import { useEffect, useState, type ReactNode } from "react";
import { Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DEFAULT_TERMINOLOGY,
  type BusinessTerminology,
  useTerminology,
} from "@/contexts/TerminologyContext";

type TerminologyPair = BusinessTerminology[keyof BusinessTerminology];

interface TerminologyRowProps {
  id: string;
  title: string;
  value: TerminologyPair;
  defaults: TerminologyPair;
  preview: ReactNode;
  disabled: boolean;
  onChange: (value: TerminologyPair) => void;
}

const TerminologyRow = ({
  id,
  title,
  value,
  defaults,
  preview,
  disabled,
  onChange,
}: TerminologyRowProps) => (
  <div className="grid gap-x-4 gap-y-2 md:grid-cols-[9rem_minmax(0,1fr)] md:items-center">
    <p className="text-sm font-medium leading-none text-foreground">{title}</p>
    <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3">
      <div>
        <Label htmlFor={`${id}-singular`} className="sr-only">{title} singular</Label>
        <Input
          id={`${id}-singular`}
          aria-label={`${title} singular`}
          className="h-9"
          value={value.singular}
          onChange={(event) => onChange({ ...value, singular: event.target.value })}
          placeholder={defaults.singular}
          maxLength={40}
          disabled={disabled}
        />
      </div>
      <span aria-hidden="true" className="text-sm text-muted-foreground">/</span>
      <div>
        <Label htmlFor={`${id}-plural`} className="sr-only">{title} plural</Label>
        <Input
          id={`${id}-plural`}
          aria-label={`${title} plural`}
          className="h-9"
          value={value.plural}
          onChange={(event) => onChange({ ...value, plural: event.target.value })}
          placeholder={defaults.plural}
          maxLength={40}
          disabled={disabled}
        />
      </div>
    </div>

    <div className="flex min-h-8 flex-wrap items-center gap-x-2 gap-y-1 rounded-md border bg-muted/20 px-3 py-1.5 md:col-start-2">
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        Preview
      </span>
      <span className="text-sm text-foreground">{preview}</span>
    </div>
  </div>
);

const cleanPair = (pair: TerminologyPair, defaults: TerminologyPair): TerminologyPair => ({
  singular: pair.singular.trim() || defaults.singular,
  plural: pair.plural.trim() || defaults.plural,
});

const TerminologySettings = () => {
  const { terminology, canEdit, saveTerminology, resetTerminology } = useTerminology();
  const [draft, setDraft] = useState<BusinessTerminology>(terminology);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    setDraft(terminology);
  }, [terminology]);

  const cleaned: BusinessTerminology = {
    customer: cleanPair(draft.customer, DEFAULT_TERMINOLOGY.customer),
    company: cleanPair(draft.company, DEFAULT_TERMINOLOGY.company),
  };
  const terms = Object.values(draft).flatMap((pair) => [pair.singular.trim(), pair.plural.trim()]);
  const invalid = terms.some((term) => !term || term.length > 40);
  const unchanged = JSON.stringify(cleaned) === JSON.stringify(terminology);
  const isDefault = JSON.stringify(terminology) === JSON.stringify(DEFAULT_TERMINOLOGY);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (invalid || !canEdit) return;
    setSaving(true);
    try {
      await saveTerminology(cleaned);
      toast.success("Terminology saved across your business");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not save terminology");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!canEdit) return;
    setResetting(true);
    try {
      await resetTerminology();
      toast.success("Terminology reset to the Dibs defaults");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not reset terminology");
    } finally {
      setResetting(false);
    }
  };

  const customerSingular = cleaned.customer.singular;
  const customerPlural = cleaned.customer.plural;
  const companySingular = cleaned.company.singular;
  const companyPlural = cleaned.company.plural;

  return (
    <main>
      <form className="space-y-8" onSubmit={save}>
        <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 space-y-1">
            <h1 className="text-xl font-heading font-semibold text-foreground">Terminology</h1>
            <p className="text-sm text-muted-foreground">
              Customize the terms your team uses across Dibs.
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="standard-action-button"
              onClick={reset}
              disabled={!canEdit || resetting || saving || isDefault}
            >
              {resetting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
              Reset defaults
            </Button>
            <Button
              type="submit"
              size="sm"
              className="standard-action-button"
              disabled={!canEdit || invalid || unchanged || saving || resetting}
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save terminology
            </Button>
          </div>
        </header>

        <section aria-labelledby="people-terminology-heading">
          <div className="mb-2 flex items-center gap-4">
            <h2 id="people-terminology-heading" className="shrink-0 font-heading text-base font-semibold text-foreground">
              People
            </h2>
            <div className="h-px flex-1 bg-border" aria-hidden="true" />
          </div>
          <TerminologyRow
            id="customer-term"
            title="Customer"
            value={draft.customer}
            defaults={DEFAULT_TERMINOLOGY.customer}
            preview={
              <>
                {customerPlural} · Import {customerPlural.toLowerCase()} · {customerSingular} profile
              </>
            }
            disabled={!canEdit}
            onChange={(customer) => setDraft((current) => ({ ...current, customer }))}
          />
        </section>

        <section aria-labelledby="business-terminology-heading">
          <div className="mb-2 flex items-center gap-4">
            <h2 id="business-terminology-heading" className="shrink-0 font-heading text-base font-semibold text-foreground">
              Business
            </h2>
            <div className="h-px flex-1 bg-border" aria-hidden="true" />
          </div>
          <TerminologyRow
            id="company-term"
            title="Company"
            value={draft.company}
            defaults={DEFAULT_TERMINOLOGY.company}
            preview={
              <>
                {companyPlural} · {companySingular} details · {companySingular} background
              </>
            }
            disabled={!canEdit}
            onChange={(company) => setDraft((current) => ({ ...current, company }))}
          />
        </section>

        {!canEdit && (
          <p className="text-sm text-muted-foreground">
            Only business owners and authorized Dibs collaborators can change business-wide terminology.
          </p>
        )}
      </form>
    </main>
  );
};

export default TerminologySettings;
